import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { CosmosDBStoreType, CosmosSqlSettings } from 'app/model/dataflow/dataset-settings/cosmos-sql-settings';
import { DatasetTypeHandler } from 'app/components/create-datamapper/utils/datasettype-handler';
import { Dataset, formatConst, LinkedService, Metamodel, Owned, storeConst, Named } from 'shell-models-lib';
import { createDataflowExpression, DataFlowExpression } from 'app/model/dataflow/dataflowexpression';
import { IMapperConnectionInfo, IMapperEntity, IMapperTable, SchemaLoadStatus, ICosmosDBMapperEntity } from 'app/model/adfDataMapper/IMapperTable';
import { DataFlowDatasetSettings, DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { DslConnectorConstants } from 'app/model/adapters/mappingArtifact/DslConnectorConstants';
import { AzureCosmosDbV2Dataset } from 'app/model/dataset/azure-cosmos-db.v2.dataset';
import { getValidTransformationName } from 'app/model/adfDataMapper/adfDataMapperUtils';
import { QueryApiFactoryService } from 'app/apis/query-api.factory.service';
import { DataFlowExpressionUtils } from 'app/model/adapters/dataflow/expression.util';
import { getDslPropertyValue } from 'app/model/adfDataMapper/mapperTable.utils';
import { AdfDataMapper } from 'app/model/adfDataMapper/adfDataMapper';
import { DataFlow } from 'app/model/dataflow/dataflow';
import { Subject, first } from 'rxjs';
import { GlobalVariables } from 'app/services/globals';
import { LoggerService } from 'app/logger/logger.service';


export class CosmosDatasetTypeHandler extends DatasetTypeHandler {
    public populateSourceProperties(inputSource: DataFlowSource, outputSource: DataFlowSource, selectedDatasetFormat: DatasetFormatType) {
        outputSource.datasetSettings = Owned.create(Metamodel.modelByName('CosmosSqlSettings'), outputSource);
        const incomingSourceDatasetsSettings = inputSource.datasetSettings as CosmosSqlSettings;
        const outputSourceDatasetsSettings = outputSource.datasetSettings as CosmosSqlSettings;
        outputSource.datasetSettings.initialize('');
        outputSourceDatasetsSettings.tableName.expression = incomingSourceDatasetsSettings.tableName.expression;
        outputSource.wildcardPaths = inputSource.wildcardPaths;
        outputSource.datasetSettings = outputSourceDatasetsSettings;
    }

    public processSelectedRows(
        selectedRows: IMapperEntity[],
        selectedSourceLinkedService: LinkedService,
        selectedDatasetFormat: DatasetFormatType): IMapperTable[] {

        const tables: IMapperTable[] = [];
        selectedRows.forEach((elem) => {
            const tableName: DataFlowExpression = Owned.create<DataFlowExpression>(DataFlowExpression.MODEL, selectedSourceLinkedService);
            tableName.expression = elem.entityName;
            const dslConnectorPropContainer = elem.entityName;
            tables.push({
                // need a valid mapping dataflow source name here to pass dataflow validations
                name: getValidTransformationName(true, elem.entityName),
                sourceLinkedService: selectedSourceLinkedService,
                isInlineDataset: true,
                sourceDataset: elem.dataset,
                // will initialize datasetSettings post processing Source Cosmos entity.
                datasetSettings: undefined,
                wildcardPaths: [],
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                dslConnectorProperties: new Map<string, any>([
                    [DslConnectorConstants.container, DataFlowExpressionUtils.escapeSingleQuoteInDslString(dslConnectorPropContainer)],
                    [DslConnectorConstants.tableName, tableName.expression]
                ]),
                datasetType: DatasetFormatType.CosmosDB,
                schemaLoadStatus: SchemaLoadStatus.NOT_STARTED,
                tableName: elem.entityName
            });
        });

        return tables;
    }

    public getOkButtonStatus(selectedRows: IMapperEntity[]): [boolean, string] {
        if (selectedRows.length > 0) {
            return [false, ''];
        }
        return [true, ''];
    }

    public getDefaultFileSettings(): DataFlowDatasetSettings {
        const dataFlowSource = DataFlowSource.MODEL.create<Named>();
        const cosmosSqlSettings = Owned.create<CosmosSqlSettings>(CosmosSqlSettings.MODEL, dataFlowSource);
        cosmosSqlSettings.initialize(undefined);
        return cosmosSqlSettings;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public getDatasetSettings(dslConnectorProperties: Map<string, any>, table: IMapperTable): DataFlowDatasetSettings {
        // Read dsl properties
        const tableName = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.tableName);

        // Create and populate cosmosSqlSettings
        const cosmosSqlSettings = Owned.create<CosmosSqlSettings>(CosmosSqlSettings.MODEL, table as DataFlowSource);
        cosmosSqlSettings.initialize(undefined);
        cosmosSqlSettings.tableName = createDataflowExpression(tableName) as DataFlowExpression;

        return cosmosSqlSettings;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public getDatasetForPreview(dslConnectorProperties: Map<string, any>, linkedService: LinkedService): Dataset {
        const tableName = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.tableName);
        const dataset = new AzureCosmosDbV2Dataset('CosmosDbSqlApiCollection');
        dataset.linkedService = linkedService;
        dataset.table = tableName;
        dataset.tableName = tableName;
        const resourceInRaw = {
            properties: {
                typeProperties: {
                    collectionName: tableName,
                }
            }
        };
        dataset.raw = JSON.stringify(resourceInRaw);
        return dataset;
    }

    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    public async fetchEntities(loggerService: LoggerService, queryApiFactoryService: QueryApiFactoryService, linkedService: LinkedService,
        isSource: boolean, selectedDatasetFormat: DatasetFormatType, isDebugSessionActive: () => Promise<boolean> =  undefined,
        getEntitiesForADataFlowSource: (source: DataFlowSource, resultSubject: Subject<ICosmosDBMapperEntity[]>) => Promise<void> = undefined): Promise<ICosmosDBMapperEntity[]> {
        //Get Transactional containers
        const oltpList = await queryApiFactoryService.getWizardQueryApi().getNavTableAsync(linkedService, undefined, undefined);
        const oltpResult: ICosmosDBMapperEntity[] = [];
        oltpList.forEach((elem) => oltpResult.push({ collectionName: elem?.name, storeType: CosmosDBStoreType.Transactional }));
        if(!GlobalVariables.enableCosmosDBASForCDC) {
            return oltpResult;
        }
        //Get Analytical containers
        const dataMappingModelOLAP: AdfDataMapper = new AdfDataMapper('datamapper');
        const dataflowOLAP: DataFlow = Named.create<DataFlow>(DataFlow.MODEL, dataMappingModelOLAP);
        const sourceOLAP: DataFlowSource = Named.create<DataFlowSource>(DataFlowSource.MODEL, dataflowOLAP);
        sourceOLAP.sourceLinkedService = linkedService;
        const resultSubjectOLAP = new Subject<ICosmosDBMapperEntity[]>();
        const cosmosSqlSettingsOLAP = Owned.create<CosmosSqlSettings>(CosmosSqlSettings.MODEL, sourceOLAP);
        cosmosSqlSettingsOLAP.initialize(undefined);
        cosmosSqlSettingsOLAP.storeType = CosmosDBStoreType.Analytical;
        sourceOLAP.datasetSettings = cosmosSqlSettingsOLAP;
        sourceOLAP.isInlineDataset = true;
        sourceOLAP.name = 'olapsrc';
        dataflowOLAP.addSource(sourceOLAP);
        dataflowOLAP.name = 'olapdf';
        sourceOLAP.addOutputStream();
        await getEntitiesForADataFlowSource(sourceOLAP, resultSubjectOLAP);
        const firstResultOLAP = resultSubjectOLAP.pipe(first()).toPromise();
        const olapList = await firstResultOLAP;
        const olapResult: ICosmosDBMapperEntity[] = [];
        olapList.forEach((elem) => olapResult.push({ collectionName: elem.collectionName, storeType: CosmosDBStoreType.Analytical }));

        return oltpResult.concat(olapResult);
    }

    public setCommonDslConnectionProperties(mapperConnectionInfo: IMapperConnectionInfo, selectedDatasetFormat: DatasetFormatType, selectedSourceFileOptions: DataFlowDatasetSettings): void {
        this.setCommonDslDriftProperties(mapperConnectionInfo);

        const commonDslConnectorProperties = mapperConnectionInfo.connection.commonDslConnectorProperties;
        commonDslConnectorProperties.set(DslConnectorConstants.format, formatConst.CosmosDB);
        commonDslConnectorProperties.set(DslConnectorConstants.store, storeConst.CosmosDB);
        commonDslConnectorProperties.set('enableChangeFeed', true);
        commonDslConnectorProperties.set('systemColumns', true);
        commonDslConnectorProperties.set('changeFeedStartFromTheBeginning', false);

        const cosmosSettings = selectedSourceFileOptions as CosmosSqlSettings;
        commonDslConnectorProperties.set('storeType', cosmosSettings.storeType);
    }

}
